import React, { PropsWithChildren, useMemo, useState } from 'react';
import theme from './Tags.module.scss';
import { ProjectTag, ProjectTagType } from '@/types';
import { faTag } from '@fortawesome/pro-light-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { classnames } from '@/utils/html';
import { Tooltip } from '@/components/Tooltip';
import { Trans } from '@lingui/react/macro';
import { Button } from '@/components/Buttons';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ygtechnologies/bygr-frontend-component-library';
import { TagInput } from '@/components/TagInput';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Badge, BadgeSize } from '@ygtechnologies/rubus-ui';
import { useRooms } from '@/services/rooms';
import { useGetProjectId } from '@/utils/hooks';
// interface PillProps extends PropsWithChildren {
//     hideIcon: boolean;
// }

// const Pill: React.FC<PillProps> = ({ children, hideIcon }) => (
//     <li className={theme.pillListItem}>
//         {!hideIcon && <FontAwesomeIcon icon={faTag} className={theme.icon} />}
//         <div className={theme.text}>{children}</div>
//     </li>
// );

export interface TagsProps {
    tags: ProjectTag[];
    size?: 'small' | 'normal';
    /** Size of the rendered badges. Defaults to the Badge default ('md') to preserve existing call sites. */
    badgeSize?: BadgeSize;
    hideIcon?: boolean;
    className?: string;
    limit?: number;
    theme?: 'light' | 'dark';
    addTags?: boolean;
    onChange?: (tags: ProjectTag[]) => void;
    canCreate?: boolean;
}
const Tags: React.FC<TagsProps> = ({
    tags,
    limit,
    size = 'normal',
    badgeSize,
    hideIcon = false,
    className,
    theme: themeName = 'light',
    addTags = false,
    onChange,
    canCreate = true,
}) => {
    // const sortedTags = useMemo(() => orderBy(tags, 'label', 'asc'), [tags]);
    const projectId = useGetProjectId();
    const roomsQuery = useRooms(projectId!);
    const shouldLimit = limit != null && tags.length > limit + 1;

    const visibleTags = shouldLimit ? tags.slice(0, limit) : tags;

    const hiddenTags = shouldLimit ? tags.slice(limit) : [];
    const [show, setShow] = useState(false);
    const [editTags, setEditTags] = useState(tags);

    const onClose = () => {
        setShow(false);
    };

    return (
        <>
            <div className={className}>
                <ul className={classnames(theme.pillList, theme[size], theme[themeName])}>
                    {visibleTags
                        .filter((x) => x.type === ProjectTagType.NewRoom)
                        .map((ss, index) => {
                            const roomIcon = roomsQuery.data?.find((r) => r.id === ss.id)?.icon;
                            const iconProp: IconProp | undefined = roomIcon ? (['fal', roomIcon] as IconProp) : undefined;
                            return (
                                <Badge
                                    variant="info"
                                    appearance="subtle"
                                    size={badgeSize}
                                    iconLeft={iconProp}
                                    key={`newroom-${ss.id}-${index}`}>
                                    {ss.label}
                                    {ss.labelNote && <small className={theme.labelNote}>({ss.labelNote})</small>}
                                </Badge>
                            );
                        })}

                    {visibleTags
                        .filter((x) => x.type !== ProjectTagType.NewRoom)
                        .map((ss, index) => (
                            <Badge size={badgeSize} iconLeft={faTag} key={`tag-${ss.id}-${index}`}>
                                {ss.label}
                            </Badge>
                        ))}
                    {hiddenTags.length > 0 && (
                        <Tooltip
                            dynamicWidth
                            content={<Tags size={size} hideIcon={hideIcon} tags={hiddenTags} theme="dark" />}>
                            <li className={theme.hiddenTags}>
                                <Trans>and {hiddenTags.length} more</Trans>
                            </li>
                        </Tooltip>
                    )}
                </ul>
                {addTags && (
                    <Button
                        color="link"
                        onClick={() => {
                            setShow(true);
                        }}>
                        <Trans>Edit tags</Trans>
                    </Button>
                )}
            </div>
            <Modal
                size="lg"
                toggle={onClose}
                isOpen={show}
                onOpen={() => {
                    setEditTags(tags);
                }}>
                <ModalHeader>
                    <Trans>Edit Tags</Trans>
                </ModalHeader>
                <ModalBody>
                    <TagInput canCreate={canCreate} value={editTags} onTagsChange={(tags) => setEditTags(tags)} />
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onClick={() => {
                            triggerHandler(onChange, editTags);
                            onClose();
                        }}>
                        Save
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default React.memo(Tags);
